// src/belediye/pages/ApplicationsPage.js

import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import usePermissions from "../hooks/usePermissions";

/* HEROICONS */
import {
  ClockIcon,
  PhotoIcon,
  DocumentTextIcon,
  MapPinIcon,
  EyeIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

import ChannelCell from "../components/applications/ChannelCell";
import ExtraInfoIcons from "../components/applications/ExtraInfoIcons";
import { isApplicationRead } from "../components/applications/readState";
import { CHANNEL_META } from "../components/icons/channelMeta";
import TrDateTime from "../components/utils/TrDateTime";
import {
  safeText,
  ensureFirstUpperTr,
  titleCaseTr,
} from "../components/utils/textFormat";

import UnifiedLoading from "../components/Loading/UnifiedLoading";
import PageHeader from "../components/utils/PageHeader";
import SearchBox from "../components/utils/SearchBox";
import FilterSheet from "../components/utils/FilterSheet";
import Pagination from "../components/utils/Pagination";
import BasvuruCipSeridi, {
  STATUS_TABS,
  KAPSAM_TABS,
} from "../components/applications/BasvuruCipSeridi";

/* REDUX */
import {
  fetchApplicationsPage,
  hydrateApplicationsFromCache,
  selectApplicationsLegacy,
  selectApplicationsPagination,
} from "../redux/slices/belediyeApplicationsSlice";

/* API — dropdown + sayaç besleme */
import {
  fetchUnits,
  fetchNeighborhoods,
  fetchApplicationStatusCounts,
} from "../services/belediyeApi";

/* STYLE */
import "./ApplicationsPage.css";
import "./ApplicationsPage.mobile.css";

const ITEMS_PER_PAGE = 50;

/* FAZ-97 madde 3 — sekme sözleşmesi PAYLAŞILAN bileşene taşındı; iki sayfa da
   aynı şeridi çiziyor. Yeniden dışa aktarım yok: çağıranlar bileşenden alır. */
const EMPTY_PAGES = STATUS_TABS.reduce((acc, t) => {
  acc[t.status] = 1;
  return acc;
}, {});

/* Modül-seviyesi cache: birim/mahalle listesini tekrar tekrar çekmemek için. */
let unitsCache = null;
let neighborhoodsCache = null;

/* -------------------------------------------------------------
   useDebounce
------------------------------------------------------------- */
const useDebounce = (value, delay = 350) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

/* -------------------------------------------------------------
   MultiCheckList — aranabilir çoklu seçim (checkbox) listesi
------------------------------------------------------------- */
const MultiCheckList = ({
  options,
  selected,
  onToggle,
  searchPlaceholder,
  loading,
}) => {
  const [query, setQuery] = useState("");

  const selectedSet = useMemo(
    () => new Set((selected || []).map(String)),
    [selected]
  );

  const filtered = useMemo(() => {
    const qq = query.trim().toLowerCase();
    if (!qq) return options;
    return options.filter((o) => String(o.name).toLowerCase().includes(qq));
  }, [options, query]);

  return (
    <div className="af-multi">
      <div className="af-multi-search-wrap">
        <MagnifyingGlassIcon className="af-multi-search-icon" />
        <input
          className="af-multi-search"
          placeholder={searchPlaceholder || "Ara…"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className="af-multi-list">
        {loading ? (
          <div className="af-multi-empty">Yükleniyor…</div>
        ) : filtered.length === 0 ? (
          <div className="af-multi-empty">Sonuç yok</div>
        ) : (
          filtered.map((o) => {
            const checked = selectedSet.has(String(o.id));
            return (
              <label
                key={o.id}
                className={`af-multi-option${checked ? " checked" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(String(o.id))}
                />
                <span className="af-multi-option-name">{o.name}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

const ApplicationsPage = ({ statusName }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { unitSlug } = useParams();
  const { scope } = usePermissions(); // FAZ-22: canonical rotada unitSlug yok → scope.mode
  // FAZ-89: kapsam çipleri izne göre süzülür (rol string'i YOK).
  const { can } = usePermissions();
  const kapsamCipleri = useMemo(
    () => KAPSAM_TABS.filter((k) => can(k.permission)),
    [can]
  );

  const { applications, loading, error } = useSelector(selectApplicationsLegacy);
  const pagination = useSelector(selectApplicationsPagination);

  const token = localStorage.getItem("accessToken");
  const userRaw = localStorage.getItem("user");

  const user = useMemo(() => {
    try {
      return userRaw ? JSON.parse(userRaw) : null;
    } catch {
      return null;
    }
  }, [userRaw]);

  const isAuthenticated = Boolean(token && user);

  /* Birim filtresi yalnız tüm-birim (başkanlık) modunda anlamlı ve çalışır;
     tek birime pinlenmiş (kendi birim slug'ıyla gezen) personel/müdür için gizli. */
  const showUnitFilter =
    unitSlug === "baskanlik" ||            // legacy/alias rota
    scope?.mode === "all_units" ||         // FAZ-22: canonical rotada başkanlık modu
    Boolean(user?.is_superuser);

  /* Kapatma-onay göstergeleri (satır rozeti + segment) yalnız modül açık VE
     kullanıcının close_request.view izni varsa görünür. */
  const showCloseApproval = useMemo(() => {
    const mods = user?.enabled_modules || {};
    const perms = user?.permissions || [];
    return Boolean(mods.close_approval) && perms.includes("close_request.view");
  }, [user]);

  /* -------------------------------------------------------------
     STATE
  ------------------------------------------------------------- */
  /* FAZ-97 madde 3 — DIŞARIDAN GELEN STATÜ.
     Yönlendirilenler sayfasındaki çip şeridi statü çipine basılınca buraya
     `state.status` ile geliyor. Rota aynı (`/yonetim/basvurular`), yalnız
     hangi sekmeyle açılacağı taşınıyor: ayrı bir rota açmak dört yeni adres
     ve dört yeni izin kapısı demek olurdu. */
  const location = useLocation();
  const [activeStatus, setActiveStatus] = useState(
    () => location.state?.status || statusName,
  );

  /* Aynı sayfadayken tekrar bir statü çipiyle gelinirse (ör. geri/ileri)
     sekme takip etsin. Değişmediğinde setState çağrılmaz — FAZ-80 dersi. */
  useEffect(() => {
    const gelen = location.state?.status;
    if (gelen) setActiveStatus((cur) => (cur === gelen ? cur : gelen));
  }, [location.state]);
  const [pageByStatus, setPageByStatus] = useState(EMPTY_PAGES);
  const currentPage = pageByStatus[activeStatus] || 1;

  const [filter, setFilter] = useState({
    q: "",
    units: [],
    neighborhoods: [],
    dateFrom: "",
    dateTo: "",
  });

  const [counts, setCounts] = useState({});
  const [openedApplications, setOpenedApplications] = useState([]);

  /* İncelemedeki içinde "Onay bekleyenler" alt-segmenti (yalnız o sekmede uygulanır) */
  const [pendingCloseOnly, setPendingCloseOnly] = useState(false);

  const [units, setUnits] = useState(unitsCache || []);
  const [neighborhoods, setNeighborhoods] = useState(neighborhoodsCache || []);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [neighborhoodsLoading, setNeighborhoodsLoading] = useState(false);

  const debouncedQ = useDebounce(filter.q, 350);

  /* Backend'e giden filtreler — hepsi sunucuda, tüm veri kümesinde. */
  const backendFilters = useMemo(
    () => ({
      ...(debouncedQ.trim() ? { q: debouncedQ.trim() } : {}),
      ...(filter.units.length ? { unit: filter.units.join(",") } : {}),
      ...(filter.neighborhoods.length
        ? { neighborhood: filter.neighborhoods.join(",") }
        : {}),
      ...(filter.dateFrom ? { date_from: filter.dateFrom } : {}),
      ...(filter.dateTo ? { date_to: filter.dateTo } : {}),
    }),
    [debouncedQ, filter.units, filter.neighborhoods, filter.dateFrom, filter.dateTo]
  );

  /* Liste isteği filtreleri = backendFilters + (yalnız İncelemedeki'de) pending_close.
     Sayaçlar (status-counts) backendFilters kullanır; pending_close SADECE listeye. */
  const listFilters = useMemo(
    () => ({
      ...backendFilters,
      ...(pendingCloseOnly && activeStatus === "işleme_alındı"
        ? { pending_close: 1 }
        : {}),
    }),
    [backendFilters, pendingCloseOnly, activeStatus]
  );

  /* -------------------------------------------------------------
     AUTH
  ------------------------------------------------------------- */
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/giris");
    }
  }, [isAuthenticated, navigate]);

  /* -------------------------------------------------------------
     LIST FETCH — yalnız aktif sekme (kendi sayfası ile). 50'şer.
  ------------------------------------------------------------- */
  useEffect(() => {
    if (!isAuthenticated) return;

    dispatch(
      hydrateApplicationsFromCache({
        status: activeStatus,
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        filters: listFilters,
      })
    );

    dispatch(
      fetchApplicationsPage({
        status: activeStatus,
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        filters: listFilters,
        loader: null,
      })
    );
  }, [dispatch, activeStatus, currentPage, listFilters, isAuthenticated]);

  /* -------------------------------------------------------------
     STATUS COUNTS — filtre + scope altında tüm statülerin sayısı.
     Hafif aggregate; 50'lik liste çekmez. Sekme rozetlerini besler.
  ------------------------------------------------------------- */
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let mounted = true;

    fetchApplicationStatusCounts(backendFilters)
      .then((d) => {
        if (mounted) setCounts(d || {});
      })
      .catch(() => {
        if (mounted) setCounts({});
      });

    return () => {
      mounted = false;
    };
  }, [backendFilters, isAuthenticated]);

  /* -------------------------------------------------------------
     UNITS + NEIGHBORHOODS (dropdown besleme, modül cache'li)
  ------------------------------------------------------------- */
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let mounted = true;

    if (showUnitFilter && !unitsCache) {
      setUnitsLoading(true);
      fetchUnits()
        .then((list) => {
          unitsCache = list;
          if (mounted) setUnits(list);
        })
        .catch(() => {
          if (mounted) setUnits([]);
        })
        .finally(() => {
          if (mounted) setUnitsLoading(false);
        });
    }

    if (!neighborhoodsCache) {
      setNeighborhoodsLoading(true);
      fetchNeighborhoods()
        .then((list) => {
          neighborhoodsCache = list;
          if (mounted) setNeighborhoods(list);
        })
        .catch(() => {
          if (mounted) setNeighborhoods([]);
        })
        .finally(() => {
          if (mounted) setNeighborhoodsLoading(false);
        });
    }

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, showUnitFilter]);


  /* -------------------------------------------------------------
     OPENED APPLICATIONS (LOCAL STORAGE)
  ------------------------------------------------------------- */
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("openedApps")) || [];
    setOpenedApplications(stored);
  }, []);

  /* -------------------------------------------------------------
     HELPERS
  ------------------------------------------------------------- */
  const pick = (...vals) =>
    vals.find((v) => v !== null && v !== undefined && v !== "");

  const getCitizenFullName = (app) => {
    const c = app?.citizen || {};
    const first = pick(c?.name, c?.first_name);
    const last = pick(c?.surname, c?.last_name);
    const full = [first, last].filter(Boolean).join(" ").trim();
    return full ? titleCaseTr(full) : "—";
  };

  const getCreatedAtRaw = (app) =>
    pick(
      app?.created_at,
      app?.createdAt,
      app?.created,
      app?.created_date,
      app?.createdDate
    );

  const getNeighborhoodName = (app) => {
    const d = app?.neighborhood_detail;
    const name =
      pick(d?.name, app?.neighborhood_name, app?.neighborhood_detail_name) || "";
    return name ? titleCaseTr(String(name)) : "—";
  };

  // FAZ-54: satır rengi artık SUNUCUDAN (read_by_me). localStorage yalnız
  // geriye uyum için — sunucu alanı gelmiyorsa eski davranış korunur.
  const isOpened = (app) => isApplicationRead(app, openedApplications);

  const isOverdue = (app) => {
    const raw = getCreatedAtRaw(app);
    const createdMs = raw ? Date.parse(raw) : NaN;
    if (Number.isNaN(createdMs)) return false;
    const diff = (Date.now() - createdMs) / (1000 * 60 * 60);
    return app.status === "yeni" && diff >= 50;
  };

  /* -------------------------------------------------------------
     FILTER MUTATIONS (filtre değişince tüm sekme sayfaları 1'e döner)
  ------------------------------------------------------------- */
  const resetPages = useCallback(() => setPageByStatus(EMPTY_PAGES), []);

  const patchFilter = useCallback(
    (patch) => {
      setFilter((prev) => ({ ...prev, ...patch }));
      resetPages();
    },
    [resetPages]
  );

  const toggleId = useCallback(
    (field, id) => {
      setFilter((prev) => {
        const arr = prev[field];
        const exists = arr.includes(id);
        return {
          ...prev,
          [field]: exists ? arr.filter((x) => x !== id) : [...arr, id],
        };
      });
      resetPages();
    },
    [resetPages]
  );

  const clearFilters = useCallback(() => {
    setFilter({ q: "", units: [], neighborhoods: [], dateFrom: "", dateTo: "" });
    setPendingCloseOnly(false);
    resetPages();
  }, [resetPages]);

  const activeFilterCount = useMemo(
    () =>
      (filter.q ? 1 : 0) +
      (filter.units.length ? 1 : 0) +
      (filter.neighborhoods.length ? 1 : 0) +
      (filter.dateFrom ? 1 : 0) +
      (filter.dateTo ? 1 : 0),
    [filter]
  );

  const isFiltered = activeFilterCount > 0;

  /* -------------------------------------------------------------
     TAB CHANGE (in-place — filtreler korunur)
  ------------------------------------------------------------- */
  const handleTabClick = (status) => {
    if (status === activeStatus) return;
    setActiveStatus(status);
  };

  const setCurrentPage = useCallback(
    (updater) => {
      setPageByStatus((prev) => {
        const cur = prev[activeStatus] || 1;
        const next = typeof updater === "function" ? updater(cur) : updater;
        return { ...prev, [activeStatus]: next };
      });
    },
    [activeStatus]
  );

  /* -------------------------------------------------------------
     ROW CLICK
  ------------------------------------------------------------- */
  const handleRowClick = (app) => {
    if (!openedApplications.includes(app.id)) {
      const updated = [...openedApplications, app.id];
      setOpenedApplications(updated);
      localStorage.setItem("openedApps", JSON.stringify(updated));
    }

    // FAZ-22 4b: canonical detay (birim localStorage/scope'tan; başkan/birim tek hedefe katlanır)
    navigate(`/yonetim/basvurular/${app.id}`);
  };

  /* -------------------------------------------------------------
     PAGINATION
  ------------------------------------------------------------- */
  const totalCount = pagination?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages, setCurrentPage]);

  const currentApps = applications;

  // Bilgi içeriği — hem mobil (başlık yanı ikon) hem masaüstü (araç çubuğu)
  // InfoPopover'ında AYNI içerik kullanılır.
  const applicationsInfoContent = (
    <>
      <ul>
        <li>
          Sekmelerle statüler arasında geçin: <b>Yeni</b> /{" "}
          <b>İncelemedeki</b> / <b>Çözülen</b> / <b>Çözülemeyen</b>. Sekme
          adının yanındaki sayı, o statüdeki başvuru sayısıdır.
        </li>
        <li>
          <b>Arama</b> tüm veride çalışır: takip no, başvuru no, konu, ad
          soyad, telefon, mahalle, adres.
        </li>
        <li>
          <b>Filtrele</b> ile birim, mahalle ve tarih aralığına göre
          daraltın; filtre tüm sekmeleri kapsar, sekme sayıları filtreye
          göre güncellenir.
        </li>
        <li>
          Kapatma-onay modülü açıksa <b>İncelemedeki</b>'de{" "}
          <b>"Onay bekleyenler"</b> ile yalnız onay bekleyen başvuruları
          görürsünüz; bu satırlar <b>"onay bekliyor"</b> etiketiyle
          işaretlenir.
        </li>
      </ul>

      <div className="ui-info-legend">
        <div className="ui-info-legend-title">Satır işaretleri</div>
        <div className="ui-info-legend-row">
          <ClockIcon className="danger" />
          <span>50+ saattir işlem görmeyen (yeni) başvuru</span>
        </div>
        <div className="ui-info-legend-row">
          <PhotoIcon />
          <span>Resim mevcut</span>
        </div>
        <div className="ui-info-legend-row">
          <DocumentTextIcon />
          <span>Belge mevcut</span>
        </div>
        <div className="ui-info-legend-row">
          <MapPinIcon />
          <span>Konum mevcut</span>
        </div>
        {/* FAZ-54 — yalnız yetkisi olan kullanıcıda görünür (izinsizde ikon
            zaten hiç çizilmez, bu satır da yanıltıcı olmasın diye koşullu). */}
        <div className="ui-info-legend-row">
          <EyeIcon />
          <span>
            Başvuruyu kimlerin okuduğu — ikona gelin (yalnız yetkiliye görünür)
          </span>
        </div>
      </div>

      {/* FAZ-53 — Kanal sütunu göstergesi (sözlükten türetilir; yeni kanal
          eklenince bu liste kendiliğinden büyür). */}
      <div className="ui-info-legend">
        <div className="ui-info-legend-title">Kanal</div>
        {Object.entries(CHANNEL_META).map(([code, meta]) => {
          const LegendIcon = meta.Icon;
          return (
            <div className="ui-info-legend-row" key={code}>
              <LegendIcon className={meta.className} />
              <span>
                {code === "halk_masasi"
                  ? "Halk Masasından (çağrı) alındı"
                  : meta.label}
              </span>
            </div>
          );
        })}
        <div className="channel-legend-note">
          Yetkiniz varsa ikona tıklayarak başvuruyu kimin oluşturduğunu
          görebilirsiniz.
        </div>
      </div>
    </>
  );

  // Kolon başlıkları (WEB) — PageHeader'ın belowToolbar slotuna verilir
  const columnHeader = (
    <div className="applications-list-header" aria-hidden="true">
      <div className="cell basvuru-no" title="Başvuru Numarası">
        Başvuru No
      </div>
      <div className="cell" title="Takip Numarası">
        Takip No
      </div>
      <div className="cell">Gönderim Tarihi</div>
      <div className="cell">Gönderen</div>
      <div className="cell unit">Birim</div>
      <div className="cell neighborhood">Mahalle</div>
      <div className="cell subject">Konu</div>
      <div className="cell icons">Ek Bilgi</div>
      <div className="cell channel" title="Başvurunun geliş kanalı">
        Kanal
      </div>
    </div>
  );

  /* -------------------------------------------------------------
     RENDER
  ------------------------------------------------------------- */
  return (
    <div className="applications-page">
      {/* STICKY: HEADER + TOOLBAR + COLUMN HEADERS — paylaşılan PageHeader */}
      <PageHeader
        title="Başvurular"
        infoTitle="Başvurular — Bilgi"
        infoContent={applicationsInfoContent}
        belowToolbar={columnHeader}
        tabs={
          /* FAZ-97 madde 3 — ŞERİT PAYLAŞILAN BİLEŞENDE.
             Yönlendirilenler ayrı bir SAYFA; şerit orada da çizilsin diye
             gövde ortaklaştı. Bu sayfanın davranışı DEĞİŞMEDİ: statü çipi
             hâlâ sayfa içinde süzüyor (rota değiştirmiyor), kapsam çipi
             hâlâ rotaya gidiyor. Bileşen yalnız ÇİZİYOR; ne yapacağını
             çağıran söylüyor. */
          <BasvuruCipSeridi
            aktifStatus={activeStatus}
            sayilar={counts}
            onStatus={handleTabClick}
            kapsamCipleri={kapsamCipleri}
            onKapsam={(k) => navigate(k.to)}
            ek={
              /* Onay bekleyenler toggle — yalnız İncelemedeki + modül/izin.
                 Sekmelerin yanında (bağlamsal), ayrı satır açmaz. */
              showCloseApproval && activeStatus === "işleme_alındı" ? (
                <button
                  type="button"
                  className={`applications-pending-toggle${
                    pendingCloseOnly ? " active" : ""
                  }`}
                  onClick={() => {
                    setPendingCloseOnly((p) => !p);
                    setCurrentPage(1);
                  }}
                  title="Sadece kapatma onayı bekleyenler"
                >
                  <span>Onay bekleyenler</span>
                  {typeof counts.pending_close === "number" && (
                    <span className="apt-count">{counts.pending_close}</span>
                  )}
                </button>
              ) : null
            }
          />
        }
      >
        <SearchBox
          value={filter.q}
          onChange={(v) => patchFilter({ q: v })}
          placeholder="Ara — takip no, başvuru no, konu, ad soyad, telefon, mahalle, adres"
        />

            <FilterSheet
              activeCount={activeFilterCount}
              onClear={clearFilters}
              title="Filtreler"
              label="Filtreler"
            >
                    {/* Sıra: (1) Tarih  (2) Birim  (3) Mahalle */}
                    <div className="afp-section">
                      <div className="afp-label">Tarih aralığı</div>
                      <div className="afp-dates">
                        <input
                          type="date"
                          className="md-input"
                          value={filter.dateFrom}
                          onChange={(e) =>
                            patchFilter({ dateFrom: e.target.value })
                          }
                        />
                        <span className="afp-dates-sep">–</span>
                        <input
                          type="date"
                          className="md-input"
                          value={filter.dateTo}
                          onChange={(e) =>
                            patchFilter({ dateTo: e.target.value })
                          }
                        />
                      </div>
                    </div>

              {showUnitFilter && (
                      <div className="afp-section">
                        <div className="afp-label">
                          Birim
                          {filter.units.length > 0 && (
                            <span className="afp-count">
                              {filter.units.length}
                            </span>
                          )}
                        </div>
                        <MultiCheckList
                          options={units}
                          selected={filter.units}
                          onToggle={(id) => toggleId("units", id)}
                          searchPlaceholder="Birim ara…"
                          loading={unitsLoading}
                        />
                      </div>
                    )}

                    <div className="afp-section">
                      <div className="afp-label">
                        Mahalle
                        {filter.neighborhoods.length > 0 && (
                          <span className="afp-count">
                            {filter.neighborhoods.length}
                          </span>
                        )}
                      </div>
                      <MultiCheckList
                        options={neighborhoods}
                        selected={filter.neighborhoods}
                        onToggle={(id) => toggleId("neighborhoods", id)}
                        searchPlaceholder="Mahalle ara…"
                        loading={neighborhoodsLoading}
                      />
                    </div>
            </FilterSheet>
      </PageHeader>

      {/* LIST */}
      <div className="applications-list-shell">
        {loading && (
          <UnifiedLoading
            visible={true}
            variant="section"
            title="Başvurular yükleniyor"
            subtitle="Liste verileri hazırlanıyor..."
          />
        )}

        <div className="applications-list">
          {error ? (
            <div className="applications-empty">{error}</div>
          ) : currentApps.length === 0 ? (
            <div className="applications-empty">
              {isFiltered
                ? "Bu kriterlere uygun başvuru bulunamadı."
                : "Bu listede başvuru bulunmuyor."}
            </div>
          ) : (
            currentApps.map((app) => {
              const opened = isOpened(app);
              const overdue = isOverdue(app);
              const pendingApproval =
                showCloseApproval && app?.has_pending_close_request;

              return (
                <div
                  key={app.id}
                  className={[
                    "application-row",
                    opened ? "opened" : "",
                    overdue ? "overdue" : "",
                    pendingApproval ? "pending-approval" : "",
                  ]
                    .join(" ")
                    .trim()}
                  onClick={() => handleRowClick(app)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleRowClick(app);
                  }}
                >
                  <div className="cell basvuru-no" data-label="Başvuru No">
                    <span className="basvuru-no-wrap">
                      {app.id}
                      {overdue && (
                        <ClockIcon
                          className="icon-clock"
                          title="50+ saattir işlem görmedi"
                        />
                      )}
                    </span>
                  </div>

                  <div className="cell" data-label="Takip No">
                    {app.tracking_number}
                  </div>

                  <div className="cell" data-label="Gönderim Tarihi">
                    <TrDateTime value={getCreatedAtRaw(app)} />
                  </div>

                  <div className="cell" data-label="Gönderen">
                    {getCitizenFullName(app)}
                  </div>

                  <div className="cell unit" data-label="Birim">
                    {app?.unit_detail?.name || "-"}
                  </div>

                  <div className="cell neighborhood" data-label="Mahalle">
                    {getNeighborhoodName(app)}
                  </div>

                  <div
                    className="cell subject"
                    data-label="Konu"
                    title={safeText(app?.subject)}
                  >
                    {ensureFirstUpperTr(app?.subject)}
                  </div>

                  <div
                    className="cell icons"
                    data-label="Ek Bilgi"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Sıra bileşenin içinde sabit: okundu ikonu HER ZAMAN
                        ilk (bkz. components/applications/ExtraInfoIcons.js). */}
                    <ExtraInfoIcons app={app} />
                    {pendingApproval && (
                      <span
                        className="app-pending-badge"
                        title="Kapatma onayı bekliyor"
                      >
                        onay bekliyor
                      </span>
                    )}
                  </div>

                  {/* FAZ-53 — Kanal: ikon herkese açık, "kim oluşturdu"
                      yalnız izinliye (backend channel_origin'i süzer). */}
                  <div
                    className="cell channel"
                    data-label="Kanal"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ChannelCell app={app} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* PAGINATION — paylaşılan bileşen (utils/Pagination) */}
      <Pagination
        page={currentPage}
        totalCount={totalCount}
        pageSize={ITEMS_PER_PAGE}
        shownCount={currentApps.length}
        onChange={setCurrentPage}
      />
    </div>
  );
};

export default ApplicationsPage;
